import { Component, inject, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { GroupItem, SidenavGroupItem, SidenavTypeEnum } from '../../models/sidebar-group.models';
import { SidenavService } from '../../services/sidenav.service';
import { TopbarService } from '../../services/topbar.service';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { CompanyRole } from '../../../models/shared/enums/company-role.enum';
import { PhotographerRole } from '../../../models/shared/enums/photographer-role.enum';

@Component({
  standalone: false,
  selector: 'bt-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss']
})
export class SidenavComponent implements OnDestroy {

  public currentGroupUrlPart: string;
  public currentItemUrlPart: string;
  public sidenavGroups: Array<SidenavGroupItem> = [];
  public allSidenavGroups: Array<SidenavGroupItem> = [
    {
      sidenavType: SidenavTypeEnum.photographer,
      groupName: 'Photographer',
      groupUrlPart: 'photographer',
      groupItems: [
        { itemName: 'Dashboard', itemUrlPart: 'dashboard', iconName: 'pie_chart', placeholder: 'Search ongoing order number...', requiredRoles: [PhotographerRole.DASHBOARD_READ] },
        { itemName: 'Shooting List', itemUrlPart: 'shooting-list', iconName: 'shopping_basket', noTopSearchBar: true, requiredRoles: [PhotographerRole.SHOOTINGLIST_READ] },
        { itemName: 'Order List', itemUrlPart: 'order-list', iconName: 'photo_camera', placeholder: 'Search order number...', placeholderWithParams: 'Search style...', requiredRoles: [PhotographerRole.ORDERLIST_READ] },
        { itemName: 'Order List Auto Sort', itemUrlPart: 'order-list-auto-sorting', iconName: 'photo_camera', placeholder: 'Search order number...', placeholderWithParams: 'Search style...', requiredRoles: [PhotographerRole.ORDERLIST_READ] },
        { itemName: 'Get Hired', itemUrlPart: 'get-hired', iconName: 'business_center', placeholder: 'Search company...', requiredRoles: [PhotographerRole.GETHIRED_READ] },
        { itemName: 'Management', itemUrlPart: 'management', iconName: 'tune', noTopSearchBar: true, requiredRoles: [PhotographerRole.MANAGEMENT_READ] },
        { itemName: 'Account', itemUrlPart: 'account', iconName: 'person', noTopSearchBar: true, requiredRoles: [PhotographerRole.ACCOUNT_READ] },
      ]
    },
    {
      sidenavType: SidenavTypeEnum.company,
      groupName: 'Company',
      groupUrlPart: 'company',
      groupItems: [
        { itemName: 'Dashboard', itemUrlPart: 'dashboard', iconName: 'pie_chart', noTopSearchBar: true, requiredRoles: [CompanyRole.DASHBOARD_READ] },
        { itemName: 'Order List', itemUrlPart: 'order-list', iconName: 'photo_camera', placeholder: 'Search order number...', placeholderWithParams: 'Search style...', requiredRoles: [CompanyRole.ORDERLIST_READ] },
        { itemName: 'Styles', itemUrlPart: 'styles', iconName: 'shirt', placeholder: 'Search style...', placeholderWithParams: 'Search color...', isCustom: true, requiredRoles: [CompanyRole.STYLES_READ] },
        { itemName: 'Photographers', itemUrlPart: 'photographers', iconName: 'groups', placeholder: 'Search photographer...', requiredRoles: [CompanyRole.PHOTOGRAPHERS_READ] },
        { itemName: 'Templates', itemUrlPart: 'templates', iconName: 'business_center', noTopSearchBar: true, requiredRoles: [CompanyRole.TEMPLATES_READ] },
        { itemName: 'Management', itemUrlPart: 'management', iconName: 'tune', noTopSearchBar: true, requiredRoles: [CompanyRole.MANAGEMENT_READ] },
        { itemName: 'Account', itemUrlPart: 'account', iconName: 'person', noTopSearchBar: true, requiredRoles: [CompanyRole.ACCOUNT_READ] },
        // { itemName: 'Settings', itemUrlPart: 'settings', iconName: 'settings', noTopSearchBar: true, requiredRoles: [] },
      ]
    }
  ];

  private subscription = new Subscription();

  readonly #iconRegistry = inject(MatIconRegistry);
  readonly #sanitizer = inject(DomSanitizer);

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private sidenavService: SidenavService,
    private topbarService: TopbarService
  ) {
    if (this.activatedRoute.snapshot) {
      if (this.activatedRoute.parent && this.activatedRoute.parent.snapshot && this.activatedRoute.parent.snapshot.url) {
        this.currentGroupUrlPart = this.activatedRoute.parent.snapshot.url.length ?
          this.activatedRoute.parent.snapshot.url[this.activatedRoute.parent.snapshot.url.length - 1].path : null;
      }
      this.activatedRoute.snapshot.data && this.activatedRoute.snapshot.data.sidenavType ?
        this.sidenavGroups = this.allSidenavGroups.filter(g => g.sidenavType === this.activatedRoute.snapshot.data.sidenavType) :
        this.sidenavGroups = this.allSidenavGroups;
    }
    if (this.activatedRoute.firstChild) {
      this.subscription.add(
        this.activatedRoute.parent.url.subscribe(() => {
          this.currentItemUrlPart = this.activatedRoute.parent.snapshot.firstChild.firstChild.url[0].path;
          setTimeout(() => {
            this.nextTopbarTitle(!!this.activatedRoute.snapshot.queryParams && !!Object.keys(this.activatedRoute.snapshot.queryParams).length);
          });
        })
      );
    }
    this.newUrlPartSubscribe();

    this.#iconRegistry.addSvgIcon(
      'shirt',
      this.#sanitizer.bypassSecurityTrustResourceUrl('assets/icons/shirt.svg')
    );
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public onClickItem(group: SidenavGroupItem, item: GroupItem) {
    this.currentGroupUrlPart = group.groupUrlPart;
    this.currentItemUrlPart = item.itemUrlPart;
    this.nextTopbarTitle();
    this.navigate();
  }

  private navigate(params?: Params) {
    this.router.navigate(['../../' + this.currentGroupUrlPart + '/' + this.currentItemUrlPart], { queryParams: params });
  }

  private newUrlPartSubscribe() {
    this.subscription.add(this.sidenavService.newItemUrlPartSubscribe.subscribe(data => {
      if (data) {
        this.currentItemUrlPart = data.urlPart;
        this.nextTopbarTitle(!!data.params && !!Object.keys(data.params).length);
        this.navigate(data.params);
      }
    }));
  }

  private nextTopbarTitle(withParams?: boolean) {
    setTimeout(() => {
      this.sidenavGroups.some((g, i) => {
        const item = g.groupItems.find(x => x.itemUrlPart === this.currentItemUrlPart);
        const flag = !!item || i === this.sidenavGroups.length - 1;
        if (flag) { this.topbarService.nextTopbarTitleData(item && !!item.noTopSearchBar ? item.itemName : '', withParams ? item!.placeholderWithParams : item!.placeholder); }
        return flag;
      });
    });
  }
}
